import { type NavigateOptions, useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import type { FieldPath, FieldValues } from 'react-hook-form';
import { z } from 'zod';
import type { ZodForm } from '@/hooks/useZodForm';
import { useCommiteeSlugNavigate } from '@/utils/router';
import { cn } from '@/utils/style';
import type { BreadcrumbProps } from '../Breadcrumb';
import { Button } from '../Button';
import { PageNavigation } from '../PageNavigation';
import type { Step } from '../Steps';
import Steps from '../Steps';

export const formStepsSearchSchema = z.object({
  step: z.coerce.number().int().min(2).optional().catch(undefined),
});

export type FormStep<TData extends FieldValues> = Step & {
  content: React.ReactNode;
  validateFields?: FieldPath<TData>[];
  dataTableStep?: boolean;
  subHeader?: React.ReactNode;
  /**
   * Blocks « Suivant » on this step. For constraints the step already displays, so the user is
   * stopped where the explanation is — rather than on a later step whose button is dead for a
   * reason shown nowhere.
   */
  disableNext?: boolean;
};

interface FormStepsPageProps<TData extends FieldValues> {
  pathLink: BreadcrumbProps;
  form: ZodForm<TData>;
  steps: FormStep<TData>[];
  hideProgress?: boolean;
  isPending?: boolean;
  submitLabel?: string;
  onCancel: () => void;
  disableSubmit?: boolean;
}

// History has no way to drop an entry, so a finished wizard walks back over its step entries and
// replaces the first one: going back from the destination then leaves the wizard. « Suivant » pushed
// those entries in this document, so the traversal is same-document and always ends in a popstate.
export function useLeaveFormSteps() {
  const router = useRouter();
  const navigate = useCommiteeSlugNavigate();

  return async (destination: NavigateOptions) => {
    const { step = 1 } = formStepsSearchSchema.parse(router.latestLocation.search);
    if (step > 1) {
      await new Promise<void>((resolve) => {
        const unsubscribe = router.history.subscribe(({ action }) => {
          if (action.type !== 'GO') return;
          unsubscribe();
          resolve();
        });
        router.history.go(1 - step);
      });
    }
    navigate({ ...destination, replace: true });
  };
}

// The step lives in the URL so the browser back walks the steps. A step is only shown once
// « Suivant » reached it in this mount: a reload or a forward into a fresh wizard has no form data.
export function FormStepsPage<TData extends FieldValues>(props: FormStepsPageProps<TData>) {
  const { pathLink, form, steps, hideProgress, isPending, submitLabel, onCancel, disableSubmit } =
    props;
  const router = useRouter();
  const navigate = useNavigate();
  const urlStep = useSearch({ strict: false, select: (search) => search.step });
  const [furthestReachedStep, setFurthestReachedStep] = useState(0);

  const lastReachableStep = Math.min(furthestReachedStep, steps.length - 1);
  const requestedStep = (urlStep ?? 1) - 1;
  const step = Math.min(requestedStep, lastReachableStep);
  const isLastStep = step === steps.length - 1;

  useEffect(() => {
    if (step === requestedStep) return;
    navigate({
      to: '.',
      search: (prev) => ({ ...prev, step: step === 0 ? undefined : step + 1 }),
      replace: true,
    });
  }, [step, requestedStep, navigate]);

  async function advanceStep() {
    if (steps[step].validateFields) {
      if (!(await form.trigger(steps[step].validateFields))) return;
    }
    if (isLastStep) {
      form.submit();
    } else {
      setFurthestReachedStep(step + 1);
      navigate({ to: '.', search: (prev) => ({ ...prev, step: step + 2 }) });
    }
  }

  const isDataTableStep = steps[step].dataTableStep;

  return (
    <div className={`flex flex-col w-full ${isDataTableStep ? 'h-full' : 'min-h-full'}`}>
      <div className="bg-white flex-none md:sticky top-0 z-20 border-b border-gray-200">
        <div
          className={cn('px-6 pt-6', {
            'pb-6': !hideProgress,
          })}
        >
          <PageNavigation pathLink={pathLink.items} />
        </div>
        <Steps hideProgress={hideProgress} steps={steps} currentStep={step} className="pb-8" />
        {steps[step].subHeader && <div className="px-6 pb-4">{steps[step].subHeader}</div>}
      </div>
      <div
        className={cn('bg-gray-50 flex-1 flex flex-col min-h-0', {
          'p-6': !isDataTableStep,
          'overflow-x-auto': isDataTableStep,
        })}
      >
        {steps[step].content}
      </div>
      <div className="flex flex-row gap-2 w-full justify-between p-3 md:sticky bottom-0 left-0 z-10 bg-white border-t border-gray-200">
        <div className="flex gap-2">
          {step > 0 && (
            <Button onClick={() => router.history.back()} variant="outline" isPending={isPending}>
              Précédent
            </Button>
          )}
          <Button onClick={onCancel} variant="outline" disabled={isPending}>
            Annuler
          </Button>
        </div>
        <Button
          onClick={advanceStep}
          variant="color"
          isPending={isPending}
          disabled={isLastStep ? disableSubmit : steps[step].disableNext}
        >
          {isLastStep ? (submitLabel ?? 'Valider') : 'Suivant'}
        </Button>
      </div>
    </div>
  );
}
